// One app as the operator sees it: a name, a logo and an ID per platform.
//
// /apps.json stays what the TVs and the backend read — one AppDescriptor row
// per (name, platform). This module only folds those rows into one AppGroup
// per name for display, and folds an edited group back into rows, so every
// existing row (unknown platforms, duplicates, per-platform logos) survives a
// save it was not part of. Projects and rooms already enable apps by NAME, so
// a group is exactly the unit they toggle.
//
// Pure functions only (type imports below) — no store, no React.

import type { AppDescriptor } from './appsSlice';
import type { UrlInfo } from '@/features/channels/channelsSlice';

// The platforms the app editor offers, in column order. The value is what a
// NEW row is written with: TEP keeps "tizen", the value the editor has always
// written and the webapp matches on a Samsung TV.
export const appPlatforms = ['android', 'philips', 'tizen', 'webos'] as const;
export type AppPlatform = typeof appPlatforms[number];

export const appPlatformCaption: Record<AppPlatform, string> = {
  android:		'apps:app:platform:android',
  philips:		'apps:app:platform:philips',
  tizen:		'apps:app:platform:tizen',
  webos:		'apps:app:platform:webos',
};

// Platforms whose TVs install apps from install_id (webapp wixp.service:
// webos and tizen).
export const installIdPlatforms: AppPlatform[] = ['tizen', 'webos'];

// Maps a stored row's platform to its editor column. Case-insensitive, and
// "TEP"/"tep" — the label older rows carry — is the TEP column. Anything else
// (orsay, typos) is not managed by the editor and is kept untouched.
export function appPlatformOf(platform: string): AppPlatform | null {
  const p = (platform || '').trim().toLowerCase();
  if (p === 'tep')
    return 'tizen';

  return (appPlatforms as readonly string[]).includes(p)
		 ?	p as AppPlatform
		 :	null;
}

export interface AppPlatformEntry {
  id:				string;
  install_id:		string;
}

export interface AppGroup {
  name:				string;
  logo:				string;
  platforms:		Partial<Record<AppPlatform, AppPlatformEntry>>;
  // Stored platforms the editor has no column for, shown read-only.
  other:			string[];
}

export function getNewAppGroup(): AppGroup {
  return { name: '', logo: '', platforms: {}, other: [] };
}

export function copyAppGroup(group: AppGroup): AppGroup {
  const platforms: Partial<Record<AppPlatform, AppPlatformEntry>> = {};
  for (const p of appPlatforms) {
    const e = group.platforms[p];
    if (e)
      platforms[p] = { id: e.id, install_id: e.install_id };
  }

  return { name: group.name, logo: group.logo, platforms, other: [...group.other] };
}

// One group per name, in order of first appearance. The first row of a
// platform is the one the editor shows and edits; later duplicates are kept
// as they are.
export function groupApps(apps: AppDescriptor[]): AppGroup[] {
  const groups: AppGroup[] = [];
  const byName = new Map<string, AppGroup>();

  for (const app of apps || []) {
    let group = byName.get(app.name);
    if (!group) {
      group = getNewAppGroup();
      group.name = app.name;
      byName.set(app.name, group);
      groups.push(group);
    }

    if (!group.logo && app.logo)
      group.logo = app.logo;

    const p = appPlatformOf(app.platform);
    if (!p) {
      if (app.platform && !group.other.includes(app.platform))
        group.other.push(app.platform);
      continue;
    }

    if (!group.platforms[p])
      group.platforms[p] = { id: app.id || '', install_id: app.install_id || '' };
  }

  return groups;
}

// Resolves a /settings/apps/:app_id route parameter: the app name, or — for
// links from before the merge — the id of any of its rows.
export function findAppGroup(groups: AppGroup[], key: string): AppGroup | null {
  return groups.find((g) => g.name === key) ||
         groups.find((g) => Object.values(g.platforms).some((e) => e?.id === key)) ||
         null;
}

export function hasAnyPlatformId(group: AppGroup): boolean {
  return appPlatforms.some((p) => (group.platforms[p]?.id || '').trim() !== '');
}

// Writes an edited group back into the row list. `oldName` is the group's
// name before editing (null for a new app); `logoChanged` says whether the
// logo field was edited, so per-platform logos are only overwritten on
// purpose.
//
// For each editor platform the first matching row is updated in place, or
// dropped when its ID was cleared; platforms that gained an ID get a new row
// at the end. Every other row of the group only follows a rename, and the
// logo when it was edited. Rows of other apps are never touched.
export function applyAppGroup(apps: AppDescriptor[], oldName: string | null, group: AppGroup, logoChanged: boolean): AppDescriptor[] {
  const name = group.name.trim();
  const logo = group.logo.trim();
  const used = new Set<AppPlatform>();
  const result: AppDescriptor[] = [];

  for (const app of apps || []) {
    if (oldName === null || app.name !== oldName) {
      result.push({ ...app });
      continue;
    }

    const rowLogo = logoChanged || !app.logo ? logo : app.logo;
    const p = appPlatformOf(app.platform);
    if (!p || used.has(p)) {
      result.push({ ...app, name, logo: rowLogo });
      continue;
    }

    used.add(p);
    const entry = group.platforms[p];
    const id = (entry?.id || '').trim();
    if (!id)
      continue;

    result.push({
      ...app,
      name,
      logo:			rowLogo,
      id,
      install_id:	(entry?.install_id || '').trim(),
    });
  }

  for (const p of appPlatforms) {
    const entry = group.platforms[p];
    const id = (entry?.id || '').trim();
    if (used.has(p) || !id)
      continue;

    result.push({
      platform:		p,
      id,
      name,
      logo,
      install_id:	(entry?.install_id || '').trim(),
    });
  }

  return result;
}

export function removeAppGroup(apps: AppDescriptor[], name: string): AppDescriptor[] {
  return (apps || []).filter((a) => a.name !== name).map((a) => ({ ...a }));
}

// ---------------------------------------------------------------------------
// Apps an admin made available to the project's managers.
//
// Stored in the project's limits.acls as "enable-app:<name>" = "1", next to
// the "enable-apps" ACL that opens the Apps tab at all. Off by default: an
// app without the key is not offered to managers.
// ---------------------------------------------------------------------------

export const appAclPrefix = 'enable-app:';

export function appAclKey(name: string): string {
  return appAclPrefix + name;
}

export function isAppAvailable(acls: UrlInfo[] | null | undefined, name: string): boolean {
  return (acls || []).some((u) => u.type === appAclKey(name) && u.url === '1');
}

export function setAppAvailable(acls: UrlInfo[] | null | undefined, name: string, available: boolean): UrlInfo[] {
  const result = (acls || []).map((u) => ({ ...u, tags: [...(u.tags || [])] }));
  const type = appAclKey(name);
  const url = available ? '1' : '';
  const existing = result.find((u) => u.type === type);
  if (existing)
    existing.url = url;
  else
    result.push({ type, url, tags: [] });

  return result;
}
